/* =========================================================================
 * Quick Add popup
 * -------------------------------------------------------------------------
 * Opens a product-page-style modal from any product card trigger:
 *
 *   <button data-quick-add-trigger data-product-url="/products/handle">
 *
 * Content is fetched once per product from:
 *   {product-url}?section_id=quick-add-product
 *
 * Requires: snippets/quick-add-modal.liquid rendered once in theme.liquid
 * ========================================================================= */
(function () {
  'use strict';

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var SECTION_ID = 'quick-add-product';
  var cache = {};

  /* ------------------------------------------------------------ helpers */

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var fmt = format || '${{amount}}';

    function defaultTo(num, size) {
      var s = '0000000000' + num;
      return s.substring(s.length - size);
    }

    function thousands(number, precision, thousand, decimal) {
      thousand = thousand === undefined ? ',' : thousand;
      decimal = decimal === undefined ? '.' : decimal;
      if (isNaN(number) || number === null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousand);
      var cts = parts[1] ? decimal + defaultTo(parts[1], precision) : '';
      return dollars + cts;
    }

    var match = fmt.match(placeholderRegex);
    switch (match && match[1]) {
      case 'amount':
        value = thousands(cents, 2);
        break;
      case 'amount_no_decimals':
        value = thousands(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = thousands(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = thousands(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = thousands(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = thousands(cents, 0, ' ', ',');
        break;
      default:
        value = thousands(cents, 2);
    }
    return fmt.replace(placeholderRegex, value);
  }

  /**
   * Work out which Shopify sections need re-rendering after a cart change.
   *
   * Priority:
   *   1. window.QuickAddCartSections — explicit override
   *   2. data-cart-sections on the modal — set from theme settings
   *   3. auto-detected from the DOM: any cart drawer / bubble element is walked
   *      up to its `#shopify-section-{id}` wrapper, which gives the real id
   *      regardless of what the theme named its files.
   */
  function cartSectionIds() {
    if (Array.isArray(window.QuickAddCartSections) && window.QuickAddCartSections.length) {
      return window.QuickAddCartSections;
    }

    var host = document.getElementById('QuickAddModal');
    var fromSettings = host && host.dataset.cartSections;
    if (fromSettings) {
      var listed = fromSettings
        .split(',')
        .map(function (s) { return s.trim(); })
        .filter(Boolean);
      if (listed.length) return listed;
    }

    var ids = [];
    var selectors = [
      'cart-drawer',
      'cart-notification',
      '[data-cart-drawer]',
      '#CartDrawer',
      '#cart-icon-bubble',
      '.cart-count-bubble',
      '[data-cart-count]'
    ];

    document.querySelectorAll(selectors.join(',')).forEach(function (el) {
      var wrapper = el.closest('[id^="shopify-section-"]');
      if (!wrapper) return;
      var id = wrapper.id.replace('shopify-section-', '');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });

    return ids;
  }

  function focusableIn(el) {
    return el.querySelectorAll(
      'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );
  }

  /* ------------------------------------------------------- custom element */

  var QuickAddModal = function () { };

  if (typeof window.HTMLElement === 'function') {
    QuickAddModal = class extends HTMLElement {
      constructor() {
        super();
        this.body = this.querySelector('[data-qam-body]');
        this.dialog = this.querySelector('.qam__dialog');
        this.loadingHTML = this.body ? this.body.innerHTML : '';
        this.opener = null;
        this.abortController = null;
        this.openToken = 0;
        this.addWatcher = null;
        this.slides = [];
        this.thumbs = [];
        this.optionRadios = [];
      }

      connectedCallback() {
        var self = this;

        this.addEventListener('click', function (event) {
          if (event.target.closest('[data-qam-close]')) {
            var overlayClick = event.target.classList.contains('qam__overlay');
            if (overlayClick && self.dataset.closeOnOverlay === 'false') return;
            self.close();
          }
        });

        document.addEventListener('keydown', function (event) {
          if (self.dataset.open !== 'true') return;
          if (event.key === 'Escape') self.close();
          if (event.key === 'Tab') self.trapFocus(event);
        });
      }

      trapFocus(event) {
        var items = focusableIn(this.dialog);
        if (!items.length) return;
        var first = items[0];
        var last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      /* ------------------------------------------------------------ open */

      async open(productUrl, opener) {
        // Every open gets a token. Anything async that finishes after a newer
        // open (or after a close) checks the token and bails, so a slow fetch
        // can never paint over newer content.
        var token = ++this.openToken;

        this.teardown();

        this.opener = opener || null;
        this.dataset.open = 'true';
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('inert');
        document.body.classList.add('qam-open');
        this.body.innerHTML = this.loadingHTML;
        this.dialog.scrollTop = 0;

        try {
          var html = cache[productUrl];
          if (!html) {
            this.abortController = new AbortController();

            var sep = productUrl.indexOf('?') > -1 ? '&' : '?';
            var res = await fetch(productUrl + sep + 'section_id=' + SECTION_ID, {
              signal: this.abortController.signal
            });
            if (!res.ok) throw new Error('HTTP ' + res.status);
            var text = await res.text();

            if (token !== this.openToken) return; // superseded

            var doc = new DOMParser().parseFromString(text, 'text/html');
            var content = doc.querySelector('[data-qam-content]');
            if (!content) throw new Error('Quick add section markup not found');
            html = content.outerHTML;
            cache[productUrl] = html;
          }

          if (token !== this.openToken) return;

          this.body.innerHTML = html;
          this.hydrate();

          var firstFocus = focusableIn(this.dialog)[0];
          if (firstFocus) firstFocus.focus({ preventScroll: true });
        } catch (error) {
          if (error.name === 'AbortError' || token !== this.openToken) return;
          console.error('[quick-add]', error);
          // Graceful degradation — send the shopper to the product page.
          window.location.href = productUrl;
        }
      }

      close() {
        // Invalidate anything still in flight for this open
        this.openToken += 1;

        if (this.opener && typeof this.opener.focus === 'function') {
          this.opener.focus({ preventScroll: true });
        }

        this.dataset.open = 'false';
        this.setAttribute('aria-hidden', 'true');
        this.setAttribute('inert', '');
        document.body.classList.remove('qam-open');

        this.teardown();

        // Drop the markup entirely. Leaving it behind keeps a second
        // <product-form>, its submit button and the payment-terms element in
        // the document, which then compete with the next open by id.
        this.body.innerHTML = this.loadingHTML;
        this.content = null;
      }

      /**
       * Release everything tied to the previously rendered product: observers,
       * listeners, timers, in-flight requests and cached element references.
       * Called on both open and close so no state can leak between products.
       */
      teardown() {
        if (this.abortController) {
          this.abortController.abort();
          this.abortController = null;
        }
        if (this.addWatcher) {
          clearInterval(this.addWatcher);
          this.addWatcher = null;
        }
        if (this.railObserver) {
          this.railObserver.disconnect();
          this.railObserver = null;
        }
        if (this.syncThumbRail) {
          window.removeEventListener('resize', this.syncThumbRail);
          this.syncThumbRail = null;
        }
        [this.submitObserver, this.spinnerObserver].forEach(function (obs) {
          if (obs) obs.disconnect();
        });
        this.submitObserver = null;
        this.spinnerObserver = null;

        if (this.body) {
          this.body.querySelectorAll('video').forEach(function (v) {
            v.pause();
          });
        }

        // Element references from the previous product — keeping these around
        // means writing the variant id into a detached form.
        this.productForm = null;
        this.themeSubmit = null;
        this.thumbsRail = null;
        this.slides = [];
        this.thumbs = [];
        this.optionRadios = [];
        this.currentVariant = null;
        this.railDragged = false;
        this.slideIndex = 0;
      }

      /* --------------------------------------------------------- hydrate */

      hydrate() {
        this.content = this.body.querySelector('[data-qam-content]');
        if (!this.content) return;

        var dataEl = this.content.querySelector('[data-qam-variants]');
        try {
          this.variants = dataEl ? JSON.parse(dataEl.textContent) : [];
        } catch (e) {
          this.variants = [];
        }

        this.moneyFormat = this.content.dataset.moneyFormat || '${{amount}}';
        this.currentVariant = this.variants.find(function (v) {
          return v.available;
        }) || this.variants[0] || null;

        this.initGallery();
        this.initOptions();
        this.initQuantity();
        this.initInfoPanel();
        this.initAddToCart();

        // Sync once so pricing / availability reflect the pre-checked options.
        if (this.content.querySelector('[data-qam-options]')) this.onOptionChange(false);
      }

      /* --------------------------------------------------------- gallery */

      initGallery() {
        var self = this;
        this.slides = Array.prototype.slice.call(this.content.querySelectorAll('[data-qam-slide]'));
        this.thumbs = Array.prototype.slice.call(this.content.querySelectorAll('[data-qam-thumb]'));
        this.thumbsRail = this.content.querySelector('[data-qam-thumbs]');
        this.slideIndex = 0;
        if (!this.slides.length) return;

        this.thumbs.forEach(function (thumb) {
          thumb.addEventListener('click', function () {
            if (self.railDragged) return; // ignore the click that ends a drag
            self.goToSlide(parseInt(thumb.dataset.qamThumb, 10));
          });
        });

        this.initThumbRail();

        var prev = this.content.querySelector('[data-qam-prev]');
        var next = this.content.querySelector('[data-qam-next]');
        if (prev) prev.addEventListener('click', function () { self.goToSlide(self.slideIndex - 1); });
        if (next) next.addEventListener('click', function () { self.goToSlide(self.slideIndex + 1); });

        // Touch swipe
        var stage = this.content.querySelector('[data-qam-stage]');
        if (stage) {
          var startX = null;
          stage.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; }, { passive: true });
          stage.addEventListener('touchend', function (e) {
            if (startX === null) return;
            var delta = e.changedTouches[0].clientX - startX;
            if (Math.abs(delta) > 40) self.goToSlide(self.slideIndex + (delta < 0 ? 1 : -1));
            startX = null;
          });
        }
      }

      /**
       * Vertical thumbnail rail: drag to scroll, arrow buttons when the rail
       * overflows, and wheel support. The rail is height-pinned to the stage,
       * so "overflowing" simply means more images than fit beside it.
       */
      initThumbRail() {
        var self = this;
        var rail = this.thumbsRail;
        if (!rail) return;

        var up = this.content.querySelector('[data-qam-thumbs-scroll="-1"]');
        var down = this.content.querySelector('[data-qam-thumbs-scroll="1"]');

        var step = function () {
          var first = self.thumbs[0];
          var gap = parseFloat(getComputedStyle(rail).rowGap || getComputedStyle(rail).gap || 10) || 10;
          return first ? first.getBoundingClientRect().height + gap : 90;
        };

        var sync = function () {
          var overflowing = rail.scrollHeight - rail.clientHeight > 2;
          rail.classList.toggle('is-draggable', overflowing);
          [up, down].forEach(function (btn) {
            if (btn) btn.hidden = !overflowing;
          });
          if (!overflowing) return;
          if (up) up.disabled = rail.scrollTop <= 1;
          if (down) down.disabled = rail.scrollTop >= rail.scrollHeight - rail.clientHeight - 1;
        };

        this.syncThumbRail = sync;

        [up, down].forEach(function (btn) {
          if (!btn) return;
          btn.addEventListener('click', function () {
            rail.scrollBy({ top: step() * parseInt(btn.dataset.qamThumbsScroll, 10) * 2, behavior: 'smooth' });
          });
        });

        rail.addEventListener('scroll', sync, { passive: true });

        // Wheel: keep vertical wheel input inside the rail
        rail.addEventListener(
          'wheel',
          function (event) {
            if (rail.scrollHeight <= rail.clientHeight) return;
            var atTop = rail.scrollTop <= 0 && event.deltaY < 0;
            var atEnd = rail.scrollTop >= rail.scrollHeight - rail.clientHeight && event.deltaY > 0;
            if (!atTop && !atEnd) event.stopPropagation();
          },
          { passive: true }
        );

        // Pointer drag to scroll
        var startY = 0;
        var startScroll = 0;
        var dragging = false;
        this.railDragged = false;

        rail.addEventListener('pointerdown', function (event) {
          if (rail.scrollHeight <= rail.clientHeight) return;
          if (event.pointerType === 'mouse' && event.button !== 0) return;
          dragging = true;
          self.railDragged = false;
          startY = event.clientY;
          startScroll = rail.scrollTop;
          rail.classList.add('is-dragging');
          rail.setPointerCapture(event.pointerId);
        });

        rail.addEventListener('pointermove', function (event) {
          if (!dragging) return;
          var delta = event.clientY - startY;
          if (Math.abs(delta) > 4) self.railDragged = true;
          rail.scrollTop = startScroll - delta;
        });

        var endDrag = function (event) {
          if (!dragging) return;
          dragging = false;
          rail.classList.remove('is-dragging');
          try {
            rail.releasePointerCapture(event.pointerId);
          } catch (e) {
            /* pointer already released */
          }
          // Let the click that follows the drag be swallowed, then reset
          setTimeout(function () {
            self.railDragged = false;
          }, 0);
        };

        rail.addEventListener('pointerup', endDrag);
        rail.addEventListener('pointercancel', endDrag);
        rail.addEventListener('pointerleave', endDrag);

        // Images load lazily, so re-measure once they settle
        sync();
        rail.querySelectorAll('img').forEach(function (img) {
          if (img.complete) return;
          img.addEventListener('load', sync, { once: true });
        });
        if (typeof ResizeObserver === 'function') {
          this.railObserver = new ResizeObserver(sync);
          this.railObserver.observe(rail);
        }
        window.addEventListener('resize', sync);
      }

      goToSlide(index) {
        if (!this.slides.length) return;
        var total = this.slides.length;
        index = ((index % total) + total) % total;
        this.slideIndex = index;

        this.slides.forEach(function (slide, i) {
          slide.classList.toggle('is-active', i === index);
          var video = slide.querySelector('video');
          if (video && i !== index) video.pause();
        });
        this.thumbs.forEach(function (thumb, i) {
          thumb.classList.toggle('is-active', i === index);
        });

        // Bring the active thumb into view by scrolling the rail itself —
        // scrollIntoView would also scroll the modal body.
        var activeThumb = this.thumbs[index];
        var rail = this.thumbsRail;
        if (activeThumb && rail && rail.scrollHeight > rail.clientHeight) {
          var top = activeThumb.offsetTop;
          var bottom = top + activeThumb.offsetHeight;
          if (top < rail.scrollTop) {
            rail.scrollTo({ top: top - 8, behavior: 'smooth' });
          } else if (bottom > rail.scrollTop + rail.clientHeight) {
            rail.scrollTo({ top: bottom - rail.clientHeight + 8, behavior: 'smooth' });
          }
        }
        if (this.syncThumbRail) this.syncThumbRail();
      }

      goToMedia(mediaId) {
        if (!mediaId) return;
        var target = this.slides.findIndex(function (slide) {
          return String(slide.dataset.mediaId) === String(mediaId);
        });
        if (target > -1) this.goToSlide(target);
      }

      /* --------------------------------------------------------- options */

      initOptions() {
        var self = this;
        this.optionRadios = Array.prototype.slice.call(this.content.querySelectorAll('.qam__radio'));
        this.optionRadios.forEach(function (radio) {
          radio.addEventListener('change', function () {
            self.onOptionChange(true);
          });
          // Block clicks on unavailable combinations
          var label = radio.nextElementSibling;
          if (label) {
            label.addEventListener('click', function (event) {
              if (radio.classList.contains('is-unavailable')) event.preventDefault();
            });
          }
        });
      }

      selectedOptions() {
        var values = [];
        if (!this.content) return values;
        this.content.querySelectorAll('[data-qam-option]').forEach(function (group) {
          var checked = group.querySelector('.qam__radio:checked');
          values.push(checked ? checked.value : null);
        });
        return values;
      }

      onOptionChange(userInitiated) {
        if (!this.content) return;
        var selected = this.selectedOptions();

        var match = this.variants.find(function (variant) {
          return selected.every(function (value, i) {
            return value === null || variant.options[i] === value;
          });
        });

        this.currentVariant = match || null;
        this.updateAvailability(selected);
        this.updatePrice();
        this.updateSelectedLabels();
        this.updateButton();
        this.updateInstallments();
        this.syncFormVariant();

        if (userInitiated && this.currentVariant && this.currentVariant.featured_media_id) {
          this.goToMedia(this.currentVariant.featured_media_id);
        }
      }

      /**
       * Greys out option values that cannot be combined with the values already
       * chosen in the preceding option groups (same behaviour as the PDP).
       */
      updateAvailability(selected) {
        if (!this.content) return;
        var self = this;
        this.content.querySelectorAll('[data-qam-option]').forEach(function (group, groupIndex) {
          group.querySelectorAll('.qam__radio').forEach(function (radio) {
            var candidate = selected.slice();
            candidate[groupIndex] = radio.value;

            var exists = self.variants.some(function (variant) {
              return candidate.every(function (value, i) {
                if (i > groupIndex) return true; // later options are free to change
                return value === null || variant.options[i] === value;
              }) && variant.available;
            });

            radio.classList.toggle('is-unavailable', !exists);
          });
        });
      }

      updateSelectedLabels() {
        if (!this.content) return;
        this.content.querySelectorAll('[data-qam-option]').forEach(function (group) {
          var label = group.querySelector('[data-qam-selected-value]');
          var checked = group.querySelector('.qam__radio:checked');
          if (label && checked) label.textContent = checked.value;
        });
      }

      updatePrice() {
        if (!this.content) return;
        var priceEl = this.content.querySelector('[data-qam-price-regular]');
        var compareEl = this.content.querySelector('[data-qam-price-compare]');
        if (!priceEl || !this.currentVariant) return;

        priceEl.textContent = formatMoney(this.currentVariant.price, this.moneyFormat);

        if (compareEl) {
          if (this.currentVariant.compare_at_price > this.currentVariant.price) {
            compareEl.textContent = formatMoney(this.currentVariant.compare_at_price, this.moneyFormat);
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }
      }

      updateInstallments() {
        if (!this.content || !this.currentVariant) return;
        var input = this.content.querySelector('[data-qam-installment-input]');
        if (input) {
          input.value = this.currentVariant.id;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        var terms = this.content.querySelector('shopify-payment-terms');
        if (terms) terms.setAttribute('variant-id', this.currentVariant.id);
      }

      updateButton() {
        if (!this.content) return;
        var button = this.content.querySelector('[data-qam-add]');
        var label = this.content.querySelector('[data-qam-atc-text]');
        if (!button || !label) return;
        var d = this.content.dataset;

        if (!this.currentVariant) {
          button.disabled = true;
          label.textContent = d.unavailableLabel || 'Unavailable';
        } else if (!this.currentVariant.available) {
          button.disabled = true;
          label.textContent = d.soldOutLabel || 'Sold out';
        } else {
          button.disabled = false;
          label.textContent = d.atcLabel || 'Add to cart';
        }
      }

      /* -------------------------------------------------------- quantity */

      initQuantity() {
        var input = this.content.querySelector('[data-qam-qty-input]');
        if (!input) return;
        this.content.querySelectorAll('[data-qam-qty]').forEach(function (button) {
          button.addEventListener('click', function () {
            var step = parseInt(button.dataset.qamQty, 10);
            var next = (parseInt(input.value, 10) || 1) + step;
            input.value = Math.max(1, next);
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });
      }

      /* ------------------------------------------------------ info panel */

      initInfoPanel() {
        var panel = this.content.querySelector('[data-qam-info-panel]');
        if (!panel) return;
        var trigger = this.content.querySelector('[data-qam-info]');
        var close = this.content.querySelector('[data-qam-info-close]');

        if (trigger) {
          trigger.addEventListener('click', function () {
            var open = panel.hidden;
            panel.hidden = !open;
            trigger.setAttribute('aria-expanded', String(open));
            if (open) panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          });
        }
        if (close) {
          close.addEventListener('click', function () {
            panel.hidden = true;
            if (trigger) trigger.setAttribute('aria-expanded', 'false');
          });
        }
      }

      /* ----------------------------------------------------- add to cart */

      initAddToCart() {
        var self = this;
        var button = this.content.querySelector('[data-qam-add]');
        if (!button) return;

        this.cartMethod = this.content.dataset.cartMethod || 'theme';
        this.productForm = document.getElementById(this.content.dataset.productFormId);
        this.themeSubmit = document.getElementById(this.content.dataset.submitButtonId);

        if (this.cartMethod === 'theme' && this.themeSubmit) {
          this.bindThemeSubmit(button);
          return;
        }

        if (this.cartMethod === 'theme') {
          console.warn(
            '[quick-add] Theme product form not found — falling back to the built-in ' +
            'cart request. Check that snippets/buy-buttons.liquid renders ' +
            '#' + this.content.dataset.submitButtonId + '.'
          );
        }

        button.addEventListener('click', function () {
          self.addToCart(button);
        });
      }

      /**
       * Mirror of the product page behaviour: the visible button just clicks
       * the hidden submit button rendered by 'buy-buttons', so the theme's own
       * product-form JS does the add, the cart drawer refresh and the errors.
       * Spinner and disabled state are synced back the same way main-product
       * does it.
       */
      bindThemeSubmit(button) {
        var self = this;
        var submit = this.themeSubmit;
        var label = this.content.querySelector('[data-qam-atc-text]');
        var spinner = this.content.querySelector('[data-qam-spinner]');
        var submitSpinner = submit.querySelector('.loading-overlay__spinner');

        var sync = function () {
          if (submitSpinner && spinner) {
            var loading = !submitSpinner.classList.contains('hidden');
            spinner.hidden = !loading;
            if (label) label.style.display = loading ? 'none' : '';
          }
          // Availability is driven by our own variant data, not the theme's
          // button, so only follow the theme while it is mid-request.
          if (submit.disabled && self.currentVariant && self.currentVariant.available) {
            button.disabled = true;
          } else {
            self.updateButton();
          }
        };

        this.submitObserver = new MutationObserver(sync);
        this.submitObserver.observe(submit, { attributes: true, attributeFilter: ['disabled', 'class'] });
        if (submitSpinner) {
          this.spinnerObserver = new MutationObserver(sync);
          this.spinnerObserver.observe(submitSpinner, { attributes: true, attributeFilter: ['class'] });
        }

        button.addEventListener('click', function (event) {
          event.preventDefault();
          if (!self.currentVariant || !self.currentVariant.available) return;

          self.syncFormVariant();
          submit.click();

          // The theme owns the request, so watch the cart instead of the fetch.
          self.watchThemeAdd();
        });

        sync();
      }

      /** Keep the theme product form pointed at the selected variant. */
      syncFormVariant() {
        if (!this.productForm || !this.currentVariant) return;
        if (!this.productForm.isConnected) return; // form from a previous open
        var input = this.productForm.querySelector('input[name="id"]');
        if (input && input.value !== String(this.currentVariant.id)) {
          input.value = this.currentVariant.id;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      /**
       * The theme handles the request, so detect completion by polling the cart
       * item count, then run the popup's own after-add behaviour.
       */
      async watchThemeAdd() {
        var self = this;
        var token = this.openToken;
        var label = this.content.querySelector('[data-qam-atc-text]');

        var readCount = async function () {
          try {
            var res = await fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } });
            var cart = await res.json();
            return cart.item_count;
          } catch (e) {
            return null;
          }
        };

        // Always read a fresh baseline — a count cached from an earlier open
        // would make the very first poll look like a successful add.
        var before = await readCount();
        if (token !== this.openToken) return;

        if (this.addWatcher) clearInterval(this.addWatcher);

        var attempts = 0;
        this.addWatcher = setInterval(async function () {
          // The popup was closed or switched products mid-request
          if (token !== self.openToken) {
            clearInterval(self.addWatcher);
            self.addWatcher = null;
            return;
          }

          attempts += 1;
          var count = await readCount();
          if (token !== self.openToken) return;

          if (count !== null && before !== null && count !== before) {
            clearInterval(self.addWatcher);
            self.addWatcher = null;
            if (label) label.textContent = self.content.dataset.cartAddedLabel || 'Added';
            self.afterAdd();
            return;
          }
          if (attempts >= 12) {
            // ~6s with no change — the theme most likely surfaced an error
            clearInterval(self.addWatcher);
            self.addWatcher = null;
            self.updateButton();
          }
        }, 500);
      }

      /** Shared post-add behaviour for both cart methods. */
      afterAdd() {
        var self = this;
        if (!this.content) return;

        var token = this.openToken;
        var after = this.content.dataset.afterAdd || 'drawer';

        if (after === 'cart') {
          window.location.href = ROOT + 'cart';
          return;
        }
        if (after === 'close' || after === 'drawer') {
          setTimeout(function () {
            // Don't close a popup the shopper has since reopened
            if (token !== self.openToken) return;
            self.close();
            if (after === 'drawer') self.openCartDrawer();
          }, 350);
        } else {
          setTimeout(function () {
            if (token === self.openToken) self.updateButton();
          }, 1600);
        }
      }

      async addToCart(button) {
        if (!this.currentVariant || !this.currentVariant.available) return;

        var qtyInput = this.content.querySelector('[data-qam-qty-input]');
        var quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
        var spinner = this.content.querySelector('[data-qam-spinner]');
        var label = this.content.querySelector('[data-qam-atc-text]');
        var errorEl = this.content.querySelector('[data-qam-error]');

        button.disabled = true;
        if (spinner) spinner.hidden = false;
        if (label) label.style.display = 'none';
        if (errorEl) errorEl.hidden = true;

        try {
          var sectionIds = cartSectionIds();

          var body = {
            items: [{ id: this.currentVariant.id, quantity: quantity }]
          };
          if (sectionIds.length) {
            body.sections = sectionIds.join(',');
            body.sections_url = window.location.pathname;
          }

          var res = await fetch(ROOT + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body)
          });
          var json = await res.json();

          if (json.status) throw new Error(json.description || json.message || 'Could not add to cart');

          // Repaint the cart UI BEFORE opening it, otherwise the drawer shows
          // whatever markup it was rendered with on page load.
          await this.refreshCartSections(json, sectionIds);
          this.notifyCart(json);

          if (label) {
            label.textContent = this.content.dataset.cartAddedLabel || 'Added';
          }

          this.afterAdd();
        } catch (error) {
          console.error('[quick-add]', error);
          if (errorEl) {
            errorEl.textContent = error.message;
            errorEl.hidden = false;
          }
          setTimeout(this.updateButton.bind(this), 1600);
        } finally {
          if (spinner) spinner.hidden = true;
          if (label) label.style.display = '';
          button.disabled = false;
        }
      }

      /**
       * Swap the freshly rendered cart sections into the page.
       *
       * `cart/add.js` returns the HTML for whichever sections we asked for, so
       * we replace each `#shopify-section-{id}` in place. If the theme exposes
       * a Dawn-style `renderContents`, we let it do the work instead.
       * Falls back to re-fetching the page when sections came back empty.
       */
      async refreshCartSections(payload, sectionIds) {
        var drawer = document.querySelector('cart-drawer, cart-notification');

        if (drawer && typeof drawer.renderContents === 'function' && payload.sections) {
          try {
            drawer.renderContents(payload);
            return;
          } catch (e) {
            // fall through to manual replacement
          }
        }

        var sections = payload.sections;

        if (!sections || !Object.keys(sections).length) {
          if (!sectionIds.length) {
            console.warn(
              '[quick-add] No cart sections detected, so the cart UI was not re-rendered. ' +
              'Set them in Theme settings → Quick add popup → "Cart section names to refresh" ' +
              '(e.g. cart-drawer, cart-icon-bubble).'
            );
            return;
          }
          try {
            var url =
              window.location.pathname + '?sections=' + encodeURIComponent(sectionIds.join(','));
            var res = await fetch(url);
            sections = await res.json();
          } catch (e) {
            return;
          }
        }

        Object.keys(sections).forEach(function (id) {
          var markup = sections[id];
          if (!markup) return;

          var target = document.getElementById('shopify-section-' + id) || document.getElementById(id);
          if (!target) return;

          var fresh = new DOMParser().parseFromString(markup, 'text/html');
          var source = fresh.getElementById('shopify-section-' + id) || fresh.body;
          if (!source) return;

          target.innerHTML = source.innerHTML;
        });
      }

      /** Tell the theme / apps that the cart changed, covering common APIs. */
      notifyCart(payload) {
        var events = ['cart:refresh', 'cart:updated', 'cart:change', 'cart:build', 'ajaxProduct:added'];
        events.forEach(function (name) {
          document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: payload }));
          document.documentElement.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: payload }));
        });

        // Dawn-style pub/sub, when present
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'quick-add', cartData: payload });
        }

        // jQuery-based themes and apps
        if (window.jQuery) {
          window.jQuery(document.body).trigger('added_to_cart', [payload]);
        }
      }

      openCartDrawer() {
        var drawer = document.querySelector('cart-drawer, cart-notification, [data-cart-drawer]');

        if (drawer) {
          if (typeof drawer.open === 'function') {
            drawer.open();
            return;
          }
          // Dawn hides the drawer with .is-empty until it has contents
          drawer.classList.remove('is-empty');
        }

        var toggle = document.querySelector(
          '[data-cart-drawer-toggle], [aria-controls="CartDrawer"], #cart-icon-bubble, ' +
          '.header__icon--cart, a[href$="/cart"].cart-icon, [data-cart-toggle]'
        );
        if (toggle) toggle.click();
      }
    };

    if (!customElements.get('quick-add-modal')) {
      customElements.define('quick-add-modal', QuickAddModal);
    }
  }

  /* ------------------------------------------------------ card triggers */

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-quick-add-trigger]');
    if (!trigger) return;

    var url = trigger.dataset.productUrl;
    if (!url) return;

    event.preventDefault();
    event.stopPropagation();

    var modal = document.getElementById('QuickAddModal');
    if (!modal || typeof modal.open !== 'function') {
      window.location.href = url;
      return;
    }
    modal.open(url, trigger);
  });
})();